import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual } from "@/lib/navegacao";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ChartContainer } from "@/components/ui/chart";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import { useDashboardAuditoria, nota1, corDaNota, type TipoAuditoria } from "@/hooks/use-auditoria";

import { PageHeader } from "@/components/layout/page-header";
import { Segmented, type SegmentedOpcao } from "@/components/layout/segmented";
// Mesma paleta fixa já usada em client/src/pages/dashboard.tsx — consistência
// de cor entre os dois dashboards do app. Cor de SÉRIE é categórica (qual
// setor/atividade), não semântica — por isso fica fora do sistema de tokens
// de propósito, igual ao original.
const CORES = ["#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6", "#0891b2", "#db2777"];

function mesAtual6(): { de: string; ate: string } {
  const hoje = new Date();
  const ate = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const d = new Date(hoje.getFullYear(), hoje.getMonth() - 5, 1);
  const de = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { de, ate };
}

function fimDoMes(mes: string): string {
  const [a, m] = mes.split("-").map(Number);
  const ultimo = new Date(a, m, 0).getDate();
  return `${mes}-${String(ultimo).padStart(2, "0")}`;
}

/** 'AAAA-MM-DD' → 'DD/MM/AA' — ano no rótulo porque o período pode atravessar virada de ano. */
function dataLabel(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a.slice(2)}`;
}

// ─── Filtro de tipo — a mesma auditoria orientativa e fechamento sempre ────
// convivem nos dados (getDashboard não filtra por tipo); isto é só a LENTE.

type TipoFiltro = "todas" | TipoAuditoria;

const OPCOES_TIPO: SegmentedOpcao<TipoFiltro>[] = [
  { id: "todas", label: "Todas" },
  { id: "orientativa", label: "Orientativas" },
  { id: "fechamento", label: "Fechamento" },
];

function filtrarPorTipo<T extends { tipo: TipoAuditoria }>(linhas: T[], filtro: TipoFiltro): T[] {
  return filtro === "todas" ? linhas : linhas.filter(l => l.tipo === filtro);
}

/**
 * Pivota linhas {data, tipo, nota} em pontos {data, dataLabel, [nome]: nota,
 * [nome__tipo]: tipo} — uma coluna por série. UM PONTO POR AUDITORIA: nunca
 * agrupa duas rodadas (orientativa + fechamento) no mesmo ponto, mesmo que
 * caiam no mesmo mês.
 */
function pivotarPorData<T extends { data: string; tipo: TipoAuditoria; nota: number | null }>(
  linhas: T[], chave: (l: T) => string, nome: (l: T) => string,
): { pontos: Array<Record<string, string | number | null>>; series: Array<{ key: string; nome: string }> } {
  const seriesMap = new Map<string, string>();
  for (const l of linhas) seriesMap.set(chave(l), nome(l));
  const series = Array.from(seriesMap.entries()).map(([key, nome]) => ({ key, nome }));

  const datas = Array.from(new Set(linhas.map(l => l.data))).sort();
  const pontos = datas.map(data => {
    const ponto: Record<string, string | number | null> = { data, dataLabel: dataLabel(data) };
    for (const s of series) {
      const l = linhas.find(x => chave(x) === s.key && x.data === data);
      ponto[s.nome] = l ? l.nota : null;
      ponto[`${s.nome}__tipo`] = l ? l.tipo : null;
    }
    return ponto;
  });
  return { pontos, series };
}

/** Vazado (círculo oco) = orientativa · cheio = fechamento. Sem ponto onde a série não tem dado nesta data. */
function PontoEvolucao(props: any) {
  const { cx, cy, payload, stroke, dataKey } = props;
  const nota = payload?.[dataKey];
  if (nota === null || nota === undefined || cx === undefined) return null;
  const tipo = payload?.[`${dataKey}__tipo`] as TipoAuditoria | null;
  const vazado = tipo === "orientativa";
  return (
    <circle cx={cx} cy={cy} r={3.5} stroke={stroke} strokeWidth={2} fill={vazado ? "hsl(var(--background))" : stroke} />
  );
}

/** Tooltip customizado — mostra o tipo de cada rodada ao lado da nota, não só o número. */
function TooltipEvolucao({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const linhas = (payload as any[]).filter(p => p.value !== null && p.value !== undefined);
  if (!linhas.length) return null;
  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md space-y-0.5">
      <p className="font-medium text-popover-foreground">{label}</p>
      {linhas.map(p => {
        const tipo = p.payload?.[`${p.dataKey}__tipo`] as TipoAuditoria | null;
        return (
          <p key={p.dataKey} className="flex items-center gap-1.5 text-popover-foreground">
            <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: p.stroke }} />
            <span className="truncate">
              {p.dataKey}: <strong>{nota1(p.value)}</strong>
              {tipo && <span className="text-muted-foreground"> ({tipo})</span>}
            </span>
          </p>
        );
      })}
    </div>
  );
}

function GraficoEvolucao({ pontos, series }: { pontos: Array<Record<string, any>>; series: Array<{ key: string; nome: string }> }) {
  if (!pontos.length || !series.length) {
    return <p className="text-sm text-muted-foreground text-center py-8">Sem auditorias fechadas no período.</p>;
  }
  const config = Object.fromEntries(series.map((s, i) => [s.nome, { label: s.nome, color: CORES[i % CORES.length] }]));
  return (
    <ChartContainer config={config} className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={pontos} margin={{ right: 10, bottom: 10, left: 0, top: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="dataLabel" tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} width={28} />
          <Tooltip content={<TooltipEvolucao />} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: "11px" }} />}
          {series.map((s, i) => (
            <Line
              key={s.key} type="monotone" dataKey={s.nome}
              stroke={CORES[i % CORES.length]} strokeWidth={2}
              dot={<PontoEvolucao />} connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

/**
 * Lista compacta: nome + notas recentes em badges, mais leve que um gráfico
 * com muitas séries no celular. Badge vazada (só borda) = orientativa; badge
 * cheia (fundo) = fechamento — mesma distinção visual do gráfico.
 */
function ListaEvolucao({ pontos, series, hrefFor }: {
  pontos: Array<Record<string, any>>; series: Array<{ key: string; nome: string }>;
  /** Só a "Evolução por responsável" liga o nome — a mesma pessoa que abre em toda tela (Ficha do mês). */
  hrefFor?: (key: string) => string;
}) {
  if (!series.length) {
    return <p className="text-sm text-muted-foreground text-center py-8">Sem dados no período.</p>;
  }
  return (
    <div className="space-y-2">
      {series.map(s => {
        const valores = pontos.map(p => ({
          dataLabel: p.dataLabel as string,
          nota: p[s.nome] as number | null,
          tipo: p[`${s.nome}__tipo`] as TipoAuditoria | null,
        }));
        const ultima = [...valores].reverse().find(v => v.nota !== null)?.nota ?? null;
        const href = hrefFor?.(s.key);
        return (
          <div key={s.key} className="rounded-md border p-2.5">
            <div className="flex items-center justify-between gap-2">
              {href ? (
                <Link href={href} state={origemAtual()} className="text-sm font-medium truncate hover:underline">{s.nome}</Link>
              ) : (
                <span className="text-sm font-medium truncate">{s.nome}</span>
              )}
              <span className={`text-lg font-bold ${corDaNota(ultima)}`}>{nota1(ultima)}</span>
            </div>
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {valores.filter(v => v.nota !== null).map(v => (
                <span
                  key={v.dataLabel}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${v.tipo === "fechamento" ? "bg-muted" : "border border-border"} ${corDaNota(v.nota)}`}
                  title={v.tipo ?? undefined}
                >
                  {v.dataLabel}: {nota1(v.nota)}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tabela comparativa — o número, não a tendência ────────────────────────
// Mesma leitura do gráfico/lista acima (usa os `pontos`/`series` já filtrados
// por tipo), só que setor a setor, rodada a rodada. Existe porque "8,4"
// (1 casa) já escondeu duas rodadas médias uma vez — aqui a casa extra é o
// ponto (8,39 vs 8,36) que decide se a queda foi geral ou de um setor só.

/** 2 casas — mais preciso que `nota1` de propósito: aqui é conferência, não leitura rápida de gráfico. */
function nota2(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : v.toFixed(2).replace(".", ",");
}

function deltaTexto(v: number): string {
  const abs = Math.abs(v).toFixed(2).replace(".", ",");
  if (v > 0) return `+${abs}`;
  if (v < 0) return `−${abs}`;
  return "0,00";
}

/** Tom semântico do sinal — nunca cor fixa (o lint do CI reprova classes Tailwind de cor cru). */
function DeltaCell({ valor }: { valor: number | null }) {
  if (valor === null) return <span className="text-muted-foreground">—</span>;
  const tom = valor > 0 ? "text-sucesso" : valor < 0 ? "text-erro" : "text-muted-foreground";
  return <span className={`font-semibold ${tom}`}>{deltaTexto(valor)}</span>;
}

/** Tipo predominante de um ponto (coluna) — as auditorias de um mesmo dia normalmente são todas do mesmo tipo. */
function tipoDoPonto(ponto: Record<string, any>, series: Array<{ key: string; nome: string }>): TipoAuditoria | null {
  for (const s of series) {
    const t = ponto[`${s.nome}__tipo`];
    if (t) return t as TipoAuditoria;
  }
  return null;
}

function TabelaComparativa({ pontos, series }: { pontos: Array<Record<string, any>>; series: Array<{ key: string; nome: string }> }) {
  if (!pontos.length || !series.length) {
    return <p className="text-sm text-muted-foreground text-center py-8">Sem auditorias no período.</p>;
  }

  const ultima = pontos.length - 1;
  const penultima = pontos.length - 2;
  const temDelta = penultima >= 0;

  const medias = pontos.map(p => {
    const notas = series.map(s => p[s.nome]).filter((n): n is number => n !== null && n !== undefined);
    return notas.length ? notas.reduce((a, b) => a + b, 0) / notas.length : null;
  });
  const deltaMedia = temDelta && medias[ultima] !== null && medias[penultima] !== null
    ? medias[ultima]! - medias[penultima]! : null;

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr>
            <th className="text-left p-1.5 sticky left-0 bg-background">Setor</th>
            {pontos.map(p => {
              const tipo = tipoDoPonto(p, series);
              return (
                <th key={p.data} className="p-1.5 text-center font-medium whitespace-nowrap">
                  <div>{p.dataLabel}</div>
                  {tipo && <div className="text-[10px] font-normal text-muted-foreground">{tipo}</div>}
                </th>
              );
            })}
            {temDelta && <th className="p-1.5 text-center font-medium whitespace-nowrap">Δ</th>}
          </tr>
        </thead>
        <tbody>
          {series.map(s => {
            const valores = pontos.map(p => p[s.nome] as number | null);
            const v1 = valores[ultima];
            const v0 = temDelta ? valores[penultima] : null;
            const delta = temDelta && v1 !== null && v1 !== undefined && v0 !== null && v0 !== undefined
              ? v1 - v0 : null;
            return (
              <tr key={s.key} className="border-t">
                <td className="p-1.5 font-medium whitespace-nowrap sticky left-0 bg-background">{s.nome}</td>
                {valores.map((v, i) => (
                  <td key={pontos[i].data} className="p-1.5 text-center">{nota2(v)}</td>
                ))}
                {temDelta && <td className="p-1.5 text-center"><DeltaCell valor={delta} /></td>}
              </tr>
            );
          })}
          <tr className="border-t-2 font-semibold">
            <td className="p-1.5 whitespace-nowrap sticky left-0 bg-background">Média</td>
            {medias.map((m, i) => (
              <td key={pontos[i].data} className="p-1.5 text-center">{nota2(m)}</td>
            ))}
            {temDelta && <td className="p-1.5 text-center"><DeltaCell valor={deltaMedia} /></td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook). `de`/`ate` vazios =
// a janela padrão de 6 meses, derivada — a URL só carrega o que o Hugo escolheu.
const PADRAO_DASH_AUDITORIA = { de: "", ate: "", tipo: "todas" };
const PERMITIDOS_DASH_AUDITORIA = { tipo: ["todas", "orientativa", "fechamento"] } as const;

export default function AuditoriaDashboard() {
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_DASH_AUDITORIA, PERMITIDOS_DASH_AUDITORIA);
  const janelaPadrao = mesAtual6();
  const deMes = filtros.de || janelaPadrao.de;
  const ateMes = filtros.ate || janelaPadrao.ate;
  const filtroTipo = filtros.tipo as TipoFiltro;
  const de = `${deMes}-01`;
  const ate = fimDoMes(ateMes);

  const { data, isLoading } = useDashboardAuditoria(de, ate);

  const evolucaoSetor = useMemo(() => {
    if (!data) return { pontos: [], series: [] };
    return pivotarPorData(filtrarPorTipo(data.evolucaoPorSetor, filtroTipo), l => l.setorId, l => l.setorNome ?? "?");
  }, [data, filtroTipo]);
  const evolucaoAtividade = useMemo(() => {
    if (!data) return { pontos: [], series: [] };
    return pivotarPorData(filtrarPorTipo(data.evolucaoPorAtividade, filtroTipo), l => l.atividadeId, l => l.atividadeNome);
  }, [data, filtroTipo]);
  const evolucaoResponsavel = useMemo(() => {
    if (!data) return { pontos: [], series: [] };
    return pivotarPorData(filtrarPorTipo(data.evolucaoPorResponsavel, filtroTipo), l => l.funcionarioId, l => l.nome);
  }, [data, filtroTipo]);

  const mapa = data?.mapaSetorAtividade ?? [];
  const setoresDoMapa = Array.from(new Set(mapa.map(m => m.setorId))).map(id => ({
    id, nome: mapa.find(m => m.setorId === id)?.setorNome ?? "?",
  }));
  const atividadesDoMapa = Array.from(new Set(mapa.map(m => m.atividadeId))).map(id => ({
    id, nome: mapa.find(m => m.atividadeId === id)?.atividadeNome ?? "?",
  }));

  const cobertura = data?.cobertura;
  const pctSetores = cobertura && cobertura.setoresTotal > 0
    ? Math.round((cobertura.setoresAuditados / cobertura.setoresTotal) * 100) : null;
  const pctCriterios = cobertura && cobertura.criteriosPossiveis > 0
    ? Math.round((cobertura.criteriosAvaliados / cobertura.criteriosPossiveis) * 100) : null;

  return (
    <div className="p-4 sm:p-6 space-y-4 pb-10">
      <PageHeader
        titulo="Dashboard de auditoria"
        descricao="Evolução da nota, cobertura e os critérios que mais puxam para baixo"
      />

      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">De</Label>
              <Input type="month" value={deMes} onChange={e => definir({ de: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Até</Label>
              <Input type="month" value={ateMes} onChange={e => definir({ ate: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tipo</Label>
            <Segmented opcoes={OPCOES_TIPO} valor={filtroTipo} onChange={v => definir({ tipo: v })} testId="seg-tipo-dashboard" />
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          {/* 6) Cobertura — primeiro, porque uma média alta sem cobertura não diz nada */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Cobertura — {cobertura?.mes ?? "—"}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 grid grid-cols-2 gap-3">
              <div className="rounded-md border p-3 text-center">
                <p className="text-2xl font-bold">{cobertura?.setoresAuditados ?? 0}<span className="text-sm text-muted-foreground">/{cobertura?.setoresTotal ?? 0}</span></p>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">setores auditados{pctSetores !== null ? ` · ${pctSetores}%` : ""}</p>
              </div>
              <div className="rounded-md border p-3 text-center">
                <p className="text-2xl font-bold">{cobertura?.criteriosAvaliados ?? 0}<span className="text-sm text-muted-foreground">/{cobertura?.criteriosPossiveis ?? 0}</span></p>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">critérios avaliados{pctCriterios !== null ? ` · ${pctCriterios}%` : ""}</p>
              </div>
            </CardContent>
          </Card>

          {/* 1) Evolução por setor — o gráfico principal */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Evolução por setor</CardTitle>
              <p className="text-xs text-muted-foreground">Um ponto por auditoria · vazado = orientativa, cheio = fechamento</p>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <GraficoEvolucao {...evolucaoSetor} />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Comparativo — o número, setor a setor</p>
                <TabelaComparativa {...evolucaoSetor} />
              </div>
            </CardContent>
          </Card>

          {/* 2) Evolução por atividade — lista no celular, mesmo dado */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Evolução por atividade</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <ListaEvolucao {...evolucaoAtividade} />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Comparativo — o número, atividade a atividade</p>
                <TabelaComparativa {...evolucaoAtividade} />
              </div>
            </CardContent>
          </Card>

          {/* 3) Evolução por responsável */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Evolução por responsável</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <ListaEvolucao
                {...evolucaoResponsavel}
                hrefFor={cobertura?.mes ? (funcionarioId) => `/equipe/ficha/${funcionarioId}?competencia=${cobertura.mes}` : undefined}
              />
            </CardContent>
          </Card>

          {/* 4) Piores critérios — a pauta de treinamento */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Piores critérios do período</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {!data?.pioresCriterios.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem critérios avaliados no período.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.pioresCriterios.map((c, i) => (
                    <div key={c.criterioId} className="flex items-center justify-between gap-2 rounded-md border p-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}º</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.nome}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.atividadeNome} · {c.avaliacoes} avaliação{c.avaliacoes === 1 ? "" : "ões"}</p>
                        </div>
                      </div>
                      <span className={`text-lg font-bold shrink-0 ${corDaNota(c.nota)}`}>{nota1(c.nota)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 5) Mapa setor × atividade — último mês do período */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Mapa setor × atividade — {cobertura?.mes ?? "—"}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {!mapa.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem auditorias fechadas no último mês do período.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="text-xs border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left p-1.5 sticky left-0 bg-background">Setor</th>
                        {atividadesDoMapa.map(a => (
                          <th key={a.id} className="p-1.5 text-center font-medium whitespace-nowrap">{a.nome}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {setoresDoMapa.map(s => (
                        <tr key={s.id} className="border-t">
                          <td className="p-1.5 font-medium whitespace-nowrap sticky left-0 bg-background">{s.nome}</td>
                          {atividadesDoMapa.map(a => {
                            const cel = mapa.find(m => m.setorId === s.id && m.atividadeId === a.id);
                            return (
                              <td key={a.id} className="p-1.5 text-center">
                                {cel ? (
                                  <span className={`font-semibold ${corDaNota(cel.nota)}`}>{nota1(cel.nota)}</span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-center">
            <Badge variant="outline" className="text-[10px]">Tudo aqui vem de auditorias FECHADAS</Badge>
          </div>
        </>
      )}
    </div>
  );
}
